import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapleGameHost } from "../../../../packages/engine-react/src";
import { Activity, Crosshair, Gauge, Radio, RotateCcw, Shield, Users } from "lucide-react";
import { createCortexGame, type CortexHudState, type CortexGameConfig } from "./cortex/createCortexGame";
import { defaultCortexOptions, type CortexOptions, type GameModeDefinition } from "./cortex/levels/types";
import { getLevel, maps } from "./cortex/levels/registry";
import { deathmatchMode } from "./cortex/modes/dm";
import { teamDeathmatchMode } from "./cortex/modes/tdm";
import { captureTheFlagMode } from "./cortex/modes/ctf";
import { CortexMainMenu, type CortexMenuScreen } from "./cortex/ui/CortexMainMenu";
import { MapBrowser } from "./cortex/ui/MapBrowser";
import { OptionsPanel } from "./cortex/ui/OptionsPanel";
import { MultiplayerHost, type HostFormValues } from "./cortex/ui/MultiplayerHost";
import { MultiplayerJoin } from "./cortex/ui/MultiplayerJoin";
import { LobbyScreen } from "./cortex/ui/LobbyScreen";
import { Label } from "@/OS/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/OS/components/ui/select";
import { publishLobbyServer, subscribeToLobbyServers, type LobbyServerRecord, type PublishedLobbyServer } from "./cortex/net/lobby";
import { CortexWebRtcHost } from "./cortex/net/webrtcHost";
import { CortexWebRtcClient } from "./cortex/net/webrtcClient";
import type { NetworkPlayerState } from "./cortex/net/protocol";

const OPTIONS_KEY = "cortex:options:v1";
const MENU_FADE_DURATION = 400;
const modeMap = {
  dm: deathmatchMode,
  tdm: teamDeathmatchMode,
  ctf: captureTheFlagMode,
} as const;

const survivalMode: GameModeDefinition = {
  id: "survival",
  name: "Arena Survival",
  shortLabel: "SP",
  description: "Singleplayer combat survival.",
  teamBased: false,
};

const initialHud: CortexHudState = {
  health: 100,
  shield: 35,
  ammo: 30,
  reserveAmmo: 120,
  wave: 1,
  score: 0,
  enemiesRemaining: 4,
  reloading: false,
  pointerLocked: false,
  sprint: 100,
  status: "Initializing Cortex...",
  weaponReady: false,
  heading: 0,
  aiming: false,
  matchLabel: "Arena Survival",
  modeLabel: "SP",
  playersConnected: 1,
  objectives: [],
};

const glowText = "text-[#ffcf41] drop-shadow-[0_0_12px_rgba(255,196,63,0.28)]";
const digitClass = `font-mono tabular-nums tracking-tight ${glowText}`;
const labelClass = "font-mono text-[11px] uppercase tracking-[0.34em] text-[#ddb652]/90 drop-shadow-[0_0_10px_rgba(255,196,63,0.18)]";
const bodyClass = "text-[#ffe6a1] drop-shadow-[0_0_12px_rgba(255,196,63,0.12)]";
const directionNames = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

function normalizeDegrees(value: number) {
  return ((value % 360) + 360) % 360;
}

function shortestAngleDelta(from: number, to: number) {
  let delta = normalizeDegrees(from - to);
  if (delta > 180) {
    delta -= 360;
  }
  return delta;
}

function getHeadingLabel(heading: number) {
  return directionNames[Math.round(heading / 45) % directionNames.length];
}

function formatCompassMark(angle: number) {
  const normalized = normalizeDegrees(angle);
  if (normalized % 45 === 0) {
    return directionNames[(normalized / 45) % directionNames.length];
  }
  return "";
}

function SegmentMeter({ value, segments, activeClass, inactiveClass = "bg-[#3a3110]/45" }: { value: number; segments: number; activeClass: string; inactiveClass?: string }) {
  const activeSegments = Math.round((Math.max(0, Math.min(100, value)) / 100) * segments);

  return (
    <div className="flex gap-0.5">
      {Array.from({ length: segments }).map((_, index) => (
        <div key={index} className={`h-2 min-w-0 flex-1 rounded-[1px] ${index < activeSegments ? activeClass : inactiveClass}`} />
      ))}
    </div>
  );
}

function FloatingStat({
  label,
  icon: Icon,
  value,
  meter,
  tone = glowText,
}: {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  value: React.ReactNode;
  meter?: React.ReactNode;
  tone?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center justify-between gap-4">
        <div className={`${labelClass} flex items-center gap-2`}>
          {Icon ? <Icon className={tone} /> : null}
          <span>{label}</span>
        </div>
      </div>
      <div>{value}</div>
      {meter ? <div className="mt-3">{meter}</div> : null}
    </div>
  );
}

function CompassHud({ heading }: { heading: number }) {
  const marks = useMemo(() => Array.from({ length: 25 }, (_, index) => index * 15), []);

  return (
    <div className="absolute left-1/2 top-6 z-20 w-[22rem] -translate-x-1/2 text-center">
      <div className="relative mx-auto h-14 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#ffcf41]/80 shadow-[0_0_10px_rgba(255,207,65,0.5)]" />
        <div className="absolute inset-x-8 top-5 h-px bg-gradient-to-r from-transparent via-[#7b621c]/70 to-transparent" />
        {marks.map((markAngle) => {
          const delta = shortestAngleDelta(markAngle, heading);
          if (Math.abs(delta) > 105) {
            return null;
          }

          const x = 50 + (delta / 90) * 42;
          const major = markAngle % 45 === 0;
          const cardinal = markAngle % 90 === 0;
          const label = formatCompassMark(markAngle);

          return (
            <div key={markAngle} className="absolute top-0 -translate-x-1/2" style={{ left: `${x}%` }}>
              <div className={`mx-auto ${major ? "h-4" : "h-2.5"} w-px bg-[#ffcf41]/80 shadow-[0_0_8px_rgba(255,207,65,0.4)]`} />
              {label ? (
                <div className={`mt-1 font-mono uppercase ${cardinal ? "text-[0.8rem]" : "text-[0.65rem]"} tracking-[0.25em] ${cardinal ? "text-[#ffcf41]" : "text-[#ddb652]/80"} drop-shadow-[0_0_8px_rgba(255,196,63,0.2)]`}>
                  {label}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex items-center justify-center gap-2">
        <div className={`${labelClass} text-[#ffcf41]`}>{getHeadingLabel(heading)}</div>
        <div className="font-mono text-[10px] tracking-[0.28em] text-[#ddb652]/85 drop-shadow-[0_0_8px_rgba(255,196,63,0.18)]">
          {Math.round(heading).toString().padStart(3, "0")}
        </div>
      </div>
    </div>
  );
}

type ActiveSession = {
  kind: "singleplayer" | "host" | "client";
  config: CortexGameConfig;
};

function loadStoredOptions() {
  if (typeof window === "undefined") {
    return defaultCortexOptions;
  }

  const raw = localStorage.getItem(OPTIONS_KEY);
  if (!raw) {
    return defaultCortexOptions;
  }

  try {
    return { ...defaultCortexOptions, ...(JSON.parse(raw) as Partial<CortexOptions>) };
  } catch {
    return defaultCortexOptions;
  }
}

const Cortex: React.FC = () => {
  const [sessionId, setSessionId] = useState(1);
  const [hud, setHud] = useState<CortexHudState>(initialHud);
  const [ready, setReady] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [menuScreen, setMenuScreen] = useState<CortexMenuScreen | null>("main");
  const [menuFadingOut, setMenuFadingOut] = useState(false);
  const [options, setOptions] = useState<CortexOptions>(loadStoredOptions);
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [selectedMapId, setSelectedMapId] = useState(maps[0]?.id ?? "arena01");
  const [selectedMissionId, setSelectedMissionId] = useState(maps[0]?.missions[0]?.id ?? "arena-survival");
  const [servers, setServers] = useState<LobbyServerRecord[]>([]);
  const [hostBusy, setHostBusy] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [lobbyServer, setLobbyServer] = useState<LobbyServerRecord | null>(null);
  const [lobbyPlayers, setLobbyPlayers] = useState<NetworkPlayerState[]>([]);
  const [lobbyStatus, setLobbyStatus] = useState<string | null>(null);
  const hostRef = useRef<CortexWebRtcHost | null>(null);
  const clientRef = useRef<CortexWebRtcClient | null>(null);
  const publishedLobbyRef = useRef<PublishedLobbyServer | null>(null);
  const pilotNameRef = useRef(`Pilot-${Math.floor(Math.random() * 900 + 100)}`);
  const gameplayHostRef = useRef<HTMLDivElement | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify(options));
  }, [options]);

  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current !== null) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (menuScreen !== null) {
      document.exitPointerLock?.();
    }
  }, [menuScreen]);

  useEffect(() => {
    if (menuScreen !== "join") {
      return;
    }

    return subscribeToLobbyServers(setServers);
  }, [menuScreen]);

  useEffect(() => {
    return () => {
      hostRef.current?.destroy();
      clientRef.current?.destroy();
      publishedLobbyRef.current?.dispose();
    };
  }, []);

  const selectedLevel = useMemo(() => getLevel(selectedMapId), [selectedMapId]);
  const selectedMission = useMemo(() => selectedLevel.missions.find((mission) => mission.id === selectedMissionId) ?? selectedLevel.missions[0], [selectedLevel, selectedMissionId]);

  const requestGameplayPointerLock = useCallback(() => {
    const canvas = gameplayHostRef.current?.querySelector("canvas");
    if (!(canvas instanceof HTMLCanvasElement)) {
      return;
    }

    const pointerLockResult = canvas.requestPointerLock?.();
    if (pointerLockResult && typeof (pointerLockResult as Promise<void>).catch === "function") {
      (pointerLockResult as Promise<void>).catch(() => undefined);
    }
  }, []);

  const beginGameplayTransition = useCallback((prepareSession?: () => void) => {
    if (fadeTimeoutRef.current !== null) {
      window.clearTimeout(fadeTimeoutRef.current);
    }

    setMenuFadingOut(true);
    prepareSession?.();

    fadeTimeoutRef.current = window.setTimeout(() => {
      setMenuScreen(null);
      setMenuFadingOut(false);
      requestGameplayPointerLock();
      fadeTimeoutRef.current = null;
    }, MENU_FADE_DURATION);
  }, [requestGameplayPointerLock]);

  const openMenu = useCallback((screen: CortexMenuScreen) => {
    if (fadeTimeoutRef.current !== null) {
      window.clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    setMenuFadingOut(false);
    setMenuScreen(screen);
  }, []);

  const restart = useCallback(() => {
    if (!activeSession) return;
    setReady(false);
    setGameOver(false);
    setHud({ ...initialHud, matchLabel: activeSession.config.level.name, modeLabel: activeSession.config.mode?.shortLabel ?? "SP" });
    setSessionId((value) => value + 1);
    setMenuFadingOut(false);
    setMenuScreen(null);
  }, [activeSession]);

  const cleanupLobby = useCallback(() => {
    hostRef.current?.destroy();
    hostRef.current = null;
    clientRef.current?.destroy();
    clientRef.current = null;
    publishedLobbyRef.current?.dispose();
    publishedLobbyRef.current = null;
    setLobbyServer(null);
    setLobbyPlayers([]);
    setLobbyStatus(null);
  }, []);

  const returnToMenu = useCallback(() => {
    cleanupLobby();
    setActiveSession(null);
    setReady(false);
    setGameOver(false);
    setHud(initialHud);
    setSessionId((value) => value + 1);
    setMenuFadingOut(false);
    setMenuScreen("main");
  }, [cleanupLobby]);

  const startSingleplayer = useCallback((levelId: string, missionId?: string) => {
    beginGameplayTransition(() => {
      cleanupLobby();
      const level = getLevel(levelId);
      const nextMission = level.missions.find((mission) => mission.id === missionId) ?? level.missions[0];
      setSelectedMapId(level.id);
      setSelectedMissionId(nextMission?.id ?? level.missions[0]?.id ?? "arena-survival");
      setActiveSession({
        kind: "singleplayer",
        config: {
          level,
          mode: survivalMode,
          missionId: nextMission?.id,
          options,
        },
      });
      setHud({ ...initialHud, matchLabel: nextMission?.name ?? level.name, modeLabel: "SP" });
      setReady(false);
      setGameOver(false);
      setSessionId((value) => value + 1);
    });
  }, [beginGameplayTransition, cleanupLobby, options]);

  const launchHostedMatch = useCallback(() => {
    if (!lobbyServer || !hostRef.current) return;

    beginGameplayTransition(() => {
      const mode = modeMap[lobbyServer.mode as keyof typeof modeMap] ?? deathmatchMode;
      const level = getLevel(lobbyServer.mapId);
      setSelectedMapId(level.id);
      hostRef.current?.startMatch();
      setActiveSession({
        kind: "host",
        config: {
          level,
          mode,
          options,
          networkAdapter: hostRef.current,
        },
      });
      setHud({ ...initialHud, matchLabel: lobbyServer.name, modeLabel: mode.shortLabel, playersConnected: Math.max(lobbyPlayers.length, 1) });
      setReady(false);
      setGameOver(false);
      setSessionId((value) => value + 1);
    });
  }, [beginGameplayTransition, lobbyPlayers.length, lobbyServer, options]);

  const startHosting = useCallback(async (values: HostFormValues) => {
    setHostBusy(true);
    setNetworkError(null);
    setLobbyStatus("Booting Realtime lobby and host transport...");
    cleanupLobby();

    try {
      const serverId = crypto.randomUUID();
      const server: LobbyServerRecord = {
        serverId,
        name: values.serverName,
        mapId: values.mapId,
        mode: values.mode,
        maxPlayers: Math.min(12, Math.max(2, values.maxPlayers)),
        currentPlayers: 1,
        hostName: values.hostName,
        createdAt: new Date().toISOString(),
      };

      const host = new CortexWebRtcHost({
        serverId,
        hostPeerId: `host-${crypto.randomUUID()}`,
        hostName: values.hostName,
        mode: values.mode,
        mapId: values.mapId,
        onPlayersChange: (players) => {
          setLobbyPlayers(players);
          setLobbyStatus(`${players.length} pilot${players.length === 1 ? "" : "s"} linked.`);
          void publishedLobbyRef.current?.update({ currentPlayers: players.length });
        },
        onError: (message) => {
          setNetworkError(message);
        },
      });

      await host.start();
      hostRef.current = host;
      setSelectedMapId(values.mapId);
      setLobbyPlayers(host.getPlayers());
      publishedLobbyRef.current = publishLobbyServer(server);
      setLobbyServer(server);
      setMenuScreen("lobby");
    } catch (error) {
      setNetworkError(error instanceof Error ? error.message : "Failed to start Cortex host.");
      cleanupLobby();
    } finally {
      setHostBusy(false);
    }
  }, [cleanupLobby]);

  const joinServer = useCallback(async (server: LobbyServerRecord) => {
    setJoinBusy(true);
    setNetworkError(null);
    setLobbyStatus(`Connecting to ${server.name}...`);
    cleanupLobby();

    try {
      const client = new CortexWebRtcClient({
        serverId: server.serverId,
        hostPeerId: server.serverId.startsWith("host-") ? server.serverId : "unknown-host",
        clientPeerId: `client-${crypto.randomUUID()}`,
        playerName: pilotNameRef.current,
        onPlayersChange: (players) => {
          setLobbyPlayers(players);
          setLobbyStatus(`${players.length} pilot${players.length === 1 ? "" : "s"} in ready room.`);
        },
        onError: (message) => {
          setNetworkError(message);
        },
        onMatchStart: ({ mode, mapId }) => {
          beginGameplayTransition(() => {
            const gameMode = modeMap[mode as keyof typeof modeMap] ?? deathmatchMode;
            const level = getLevel(mapId);
            setSelectedMapId(level.id);
            setActiveSession({
              kind: "client",
              config: {
                level,
                mode: gameMode,
                options,
                networkAdapter: client,
              },
            });
            setHud({ ...initialHud, matchLabel: server.name, modeLabel: gameMode.shortLabel, playersConnected: Math.max(lobbyPlayers.length, 2) });
            setReady(false);
            setGameOver(false);
            setSessionId((value) => value + 1);
          });
        },
        onHostQuit: () => {
          setLobbyStatus("Host ended the session.");
          setActiveSession(null);
          setMenuFadingOut(false);
          setMenuScreen("main");
          cleanupLobby();
        },
      });

      clientRef.current = client;
      setSelectedMapId(server.mapId);
      setLobbyServer(server);
      setLobbyPlayers([{ id: pilotNameRef.current, name: pilotNameRef.current, ready: false }]);
      setMenuScreen("lobby");
      await client.start();
    } catch (error) {
      setNetworkError(error instanceof Error ? error.message : "Failed to join server.");
      cleanupLobby();
    } finally {
      setJoinBusy(false);
    }
  }, [beginGameplayTransition, cleanupLobby, lobbyPlayers.length, options]);

  const createGame = useCallback(
    (engine: Parameters<typeof createCortexGame>[0]) => {
      if (!activeSession) {
        return () => undefined;
      }

      return createCortexGame(engine, activeSession.config, {
        onHudChange: setHud,
        onReadyChange: setReady,
        onGameOverChange: setGameOver,
        onPauseRequest: () => openMenu("pause"),
        isPaused: () => menuScreen !== null,
      });
    },
    [activeSession, menuScreen, openMenu]
  );

  const createBackgroundGame = useCallback(
    (engine: Parameters<typeof createCortexGame>[0]) => createCortexGame(engine, {
      level: selectedLevel,
      mode: survivalMode,
      missionId: selectedMission?.id,
      options,
      backgroundMode: true,
    }),
    [options, selectedLevel, selectedMission?.id]
  );

  const ammoRatio = useMemo(() => Math.round((hud.ammo / 30) * 100), [hud.ammo]);
  const shieldRatio = useMemo(() => Math.min(100, hud.shield * 2), [hud.shield]);
  const liveState = gameOver ? "System failure" : hud.pointerLocked ? "Combat live" : activeSession ? "Standby" : "Menu";
  const weaponState = hud.reloading ? "Reloading" : hud.weaponReady ? "Ready" : "Booting";
  const hostInitialValues = useMemo<HostFormValues>(() => ({
    serverName: "Cortex Arena Northstar",
    hostName: pilotNameRef.current,
    mode: "dm",
    mapId: selectedMapId,
    maxPlayers: 12,
  }), [selectedMapId]);

  const renderMenuContent = () => {
    if (!menuScreen || menuScreen === "main" || menuScreen === "pause") {
      return null;
    }

    if (menuScreen === "singleplayer") {
      return (
        <div className="space-y-8">
          <MapBrowser maps={maps} selectedMapId={selectedMapId} onSelect={(mapId) => {
            const level = getLevel(mapId);
            setSelectedMapId(mapId);
            setSelectedMissionId(level.missions[0]?.id ?? "arena-survival");
          }} onStart={() => startSingleplayer(selectedMapId, selectedMissionId)} startLabel="Deploy" />
          <div className="max-w-xl space-y-4 border-t border-white/10 pt-6">
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/60">Mission</Label>
              <Select value={selectedMissionId} onValueChange={setSelectedMissionId}>
                <SelectTrigger className="border-white/10 bg-black/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedLevel.missions.map((mission) => (
                    <SelectItem key={mission.id} value={mission.id}>{mission.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm leading-7 text-white/58">{selectedMission?.description}</p>
          </div>
        </div>
      );
    }

    if (menuScreen === "multiplayer") {
      return (
        <div className="flex max-w-md flex-col border-t border-white/10 pt-4">
          <button type="button" className="flex items-center justify-between border-b border-white/10 py-3 text-left font-mono text-sm uppercase tracking-[0.34em] text-white/88 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]" onClick={() => openMenu("host")}>
            <span>Host Match</span>
            <span className="text-[10px] tracking-[0.28em] text-white/40">CREATE</span>
          </button>
          <button type="button" className="flex items-center justify-between border-b border-white/10 py-3 text-left font-mono text-sm uppercase tracking-[0.34em] text-white/88 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]" onClick={() => openMenu("join")}>
            <span>Join Match</span>
            <span className="text-[10px] tracking-[0.28em] text-white/40">BROWSE</span>
          </button>
        </div>
      );
    }

    if (menuScreen === "host") {
      return <MultiplayerHost maps={maps} initialValues={hostInitialValues} isBusy={hostBusy} error={networkError} onBack={() => openMenu("multiplayer")} onHost={startHosting} />;
    }

    if (menuScreen === "join") {
      return <MultiplayerJoin maps={maps} servers={servers} isBusy={joinBusy} error={networkError} onBack={() => openMenu("multiplayer")} onJoin={joinServer} />;
    }

    if (menuScreen === "load-map") {
      return <MapBrowser maps={maps} selectedMapId={selectedMapId} onSelect={setSelectedMapId} onStart={() => startSingleplayer(selectedMapId, selectedMissionId)} startLabel="Start Sandbox" />;
    }

    if (menuScreen === "options") {
      return <OptionsPanel options={options} onChange={setOptions} onBack={() => openMenu(activeSession ? "pause" : lobbyServer ? "lobby" : "main")} />;
    }

    if (menuScreen === "lobby" && lobbyServer) {
      return <LobbyScreen server={lobbyServer} players={lobbyPlayers} isHost={Boolean(hostRef.current)} onLeave={() => {
        cleanupLobby();
        openMenu("multiplayer");
      }} onOpenOptions={() => openMenu("options")} onStartMatch={hostRef.current ? launchHostedMatch : undefined} status={lobbyStatus ?? networkError} />;
    }

    return null;
  };

  return (
    <div className="h-full w-full bg-black text-white">
      <div className="relative h-full w-full overflow-hidden bg-black">
        {menuScreen !== null && !activeSession ? (
          <MapleGameHost
            appId={`cortex-menu-${selectedMapId}`}
            targetFPS={60}
            renderMode="webgl"
            createGame={createBackgroundGame}
            className="h-full w-full"
          />
        ) : null}

        {activeSession ? (
          <div ref={gameplayHostRef} className="h-full w-full">
            <MapleGameHost
              appId={`cortex-${sessionId}`}
              targetFPS={activeSession.config.options.frameLimiter}
              renderMode="webgl"
              createGame={createGame}
              className="h-full w-full"
            />
          </div>
        ) : null}

        {!activeSession && menuScreen === null ? (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#10172a,transparent_60%),linear-gradient(180deg,#090b10,#050608)]" />
        ) : null}

        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_54%,rgba(0,0,0,0.1)_78%,rgba(0,0,0,0.38)_100%)]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#120d00]/58 via-[#120d00]/16 to-transparent" />

        {activeSession && !menuScreen ? (
          <div className="pointer-events-none absolute inset-0 z-20">
            <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-90 transition-all duration-150 ${hud.aiming ? "h-3.5 w-3.5" : "h-5 w-5"}`}>
              <div className={`absolute left-1/2 top-0 -translate-x-1/2 bg-[#ffd24f] shadow-[0_0_10px_rgba(255,210,79,0.7)] transition-all duration-150 ${hud.aiming ? "h-1 w-px" : "h-1.5 w-px"}`} />
              <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 bg-[#ffd24f] shadow-[0_0_10px_rgba(255,210,79,0.7)] transition-all duration-150 ${hud.aiming ? "h-1 w-px" : "h-1.5 w-px"}`} />
              <div className={`absolute left-0 top-1/2 -translate-y-1/2 bg-[#ffd24f] shadow-[0_0_10px_rgba(255,210,79,0.7)] transition-all duration-150 ${hud.aiming ? "h-px w-1" : "h-px w-1.5"}`} />
              <div className={`absolute right-0 top-1/2 -translate-y-1/2 bg-[#ffd24f] shadow-[0_0_10px_rgba(255,210,79,0.7)] transition-all duration-150 ${hud.aiming ? "h-px w-1" : "h-px w-1.5"}`} />
            </div>

            <CompassHud heading={hud.heading} />

            <div className="absolute left-7 top-7 max-w-[28rem]">
              <div className={labelClass}>Cortex Combat Interface</div>
              <div className={`mt-2 ${labelClass} ${glowText}`}>{liveState}</div>
              <div className={`mt-2 ${labelClass}`}>{hud.matchLabel} • {hud.modeLabel}</div>
              <div className={`mt-3 text-sm leading-6 ${bodyClass}`}>{hud.status}</div>
              {hud.objectives.length ? (
                <div className="mt-4 space-y-2 rounded-lg border border-white/10 bg-black/25 p-4">
                  <div className={labelClass}>Objectives</div>
                  {hud.objectives.map((objective) => (
                    <div key={objective.id} className="flex items-center justify-between gap-3 text-sm text-zinc-200">
                      <span>{objective.text}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300">{objective.status}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="absolute right-7 top-7 text-right">
              <div className={`${labelClass} ${glowText}`}>{weaponState}</div>
              <div className="mt-2">
                <div className={labelClass}>Score</div>
                <div className={`${digitClass} mt-1 text-3xl leading-none`}>{hud.score}</div>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2 text-[#9fe6ff] drop-shadow-[0_0_12px_rgba(120,220,255,0.18)]">
                <Users className="h-4 w-4" />
                <span className="font-mono text-sm uppercase tracking-[0.28em]">{hud.playersConnected} Linked</span>
              </div>
              <div className="mt-2 flex items-center justify-end gap-2 text-[#d3b1ff] drop-shadow-[0_0_12px_rgba(188,132,255,0.18)]">
                <Radio className="h-4 w-4" />
                <span className="font-mono text-sm uppercase tracking-[0.28em]">{hud.modeLabel}</span>
              </div>
            </div>

            <div className="absolute bottom-7 left-7 flex w-auto flex-col gap-3">
              <div className="w-[7rem]">
                <FloatingStat label="Aux" icon={Gauge} tone="text-[#b185ff] drop-shadow-[0_0_12px_rgba(160,108,255,0.24)]" value={<div className="font-mono text-[1.1rem] uppercase tracking-[0.22em] text-[#f1d16e] drop-shadow-[0_0_10px_rgba(255,196,63,0.2)]">Sprint</div>} meter={<div className="w-24"><SegmentMeter value={hud.sprint} segments={6} activeClass="bg-[#a06cff] shadow-[0_0_8px_rgba(160,108,255,0.5)]" inactiveClass="bg-[#241733]/50" /></div>} />
              </div>

              <div className="grid grid-cols-[8.5rem_8.5rem] gap-3">
                <FloatingStat label="Health" icon={Activity} value={<div className={`${digitClass} text-5xl leading-none`}>{hud.health}</div>} meter={<div className="w-24"><SegmentMeter value={hud.health} segments={8} activeClass="bg-[#ffcf41] shadow-[0_0_8px_rgba(255,207,65,0.45)]" /></div>} />
                <FloatingStat label="Shield" icon={Shield} tone="text-[#8be2ff] drop-shadow-[0_0_12px_rgba(89,211,255,0.24)]" value={<div className={`${digitClass} text-5xl leading-none text-[#8be2ff] drop-shadow-[0_0_14px_rgba(89,211,255,0.22)]`}>{hud.shield}</div>} meter={<div className="w-24"><SegmentMeter value={shieldRatio} segments={8} activeClass="bg-[#8be2ff] shadow-[0_0_8px_rgba(89,211,255,0.42)]" inactiveClass="bg-[#173142]/45" /></div>} />
              </div>
            </div>

            <div className="absolute bottom-7 right-7 flex w-auto flex-col items-end gap-4 text-right">
              <div>
                <div className={labelClass}>Weapon</div>
                <div className={`mt-1 ${labelClass} ${bodyClass}`}>Pulse / hitscan</div>
                <div className={`mt-1 ${labelClass} ${glowText}`}>{weaponState}</div>
              </div>

              <div>
                <div className="flex items-end justify-end gap-2">
                  <span className={`${labelClass} pb-1`}>Ammo</span>
                  <span className={`${digitClass} text-6xl leading-none`}>{hud.ammo}</span>
                </div>
                <div className="mt-1 flex items-end justify-end gap-2">
                  <span className={labelClass}>Reserve</span>
                  <span className={`${digitClass} text-3xl leading-none`}>{hud.reserveAmmo}</span>
                </div>
                <div className="mt-2 ml-auto w-24">
                  <SegmentMeter value={ammoRatio} segments={8} activeClass="bg-[#ffcf41] shadow-[0_0_8px_rgba(255,207,65,0.45)]" />
                </div>
              </div>
            </div>

            {!hud.pointerLocked && (
              <div className="absolute inset-0 z-30 flex items-center justify-center">
                <div className="px-8 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center text-[#ffcf41] drop-shadow-[0_0_18px_rgba(255,207,65,0.22)]">
                    <Crosshair className="h-8 w-8" />
                  </div>
                  <div className={`mt-4 ${labelClass}`}>Cortex Live</div>
                  <h3 className="mt-3 text-4xl font-semibold text-[#fff0bf] drop-shadow-[0_0_22px_rgba(255,207,65,0.12)]">STANDBY</h3>
                  <p className={`mt-4 text-base leading-7 ${bodyClass}`}>Click to Play.</p>
                  <div className={`mt-4 ${labelClass} ${glowText}`}>{ready ? "Weapon rig synced" : "Loading weapon rig"}</div>
                  {gameOver && <div className="mt-2 font-mono text-xs uppercase tracking-[0.32em] text-[#ffb399] drop-shadow-[0_0_12px_rgba(255,130,90,0.18)]">Run failed</div>}
                  <button onClick={restart} className="pointer-events-auto mt-6 font-mono text-sm uppercase tracking-[0.34em] text-[#ffcf41] drop-shadow-[0_0_14px_rgba(255,196,63,0.24)] transition-opacity hover:opacity-80">
                    <span className="inline-flex items-center gap-2">
                      <RotateCcw className="h-4 w-4" />
                      Restart Run
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {menuScreen ? (
          <CortexMainMenu screen={menuScreen} sessionActive={Boolean(activeSession)} onNavigate={openMenu} onResume={activeSession ? () => beginGameplayTransition() : undefined} onReturnToMenu={activeSession ? returnToMenu : undefined} isFadingOut={menuFadingOut}>
            {renderMenuContent()}
          </CortexMainMenu>
        ) : null}
      </div>
    </div>
  );
};

export default Cortex;